
import { CheckCircle, LucideMonitor, Wifi, Database, Laptop } from "lucide-react";

interface ServiceCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  features: { feature: string; description: string }[];
  testimonial: string;
  author: string;
}

const ServiceCard = ({ icon: Icon, title, description, features, testimonial, author }: ServiceCardProps) => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-all duration-300 hover:border-edgefi-purple/20 group">
      <div className="mb-6 w-14 h-14 rounded-full bg-edgefi-purple/10 flex items-center justify-center group-hover:bg-edgefi-purple/20 transition-colors duration-300">
        <Icon className="h-7 w-7 text-edgefi-purple" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">{title}</h3>
      <p className="text-gray-600 mb-6">
        {description}
      </p>
      
      <div className="space-y-4">
        {features.map((item, index) => (
          <div key={index} className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-gray-800 font-medium">{item.feature}</p>
              <p className="text-gray-600 text-sm mt-1">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>
      
      <div className="pt-6 mt-6 border-t border-gray-100">
        <h4 className="text-gray-900 font-semibold mb-3">How This Transforms Your Business:</h4>
        <p className="text-gray-600 italic">
          "{testimonial}" 
          <span className="text-sm not-italic block mt-2">— {author}</span>
        </p>
      </div>
    </div>
  );
};

export const ApproachSection = () => {
  const services = [
    {
      icon: LucideMonitor,
      title: "Peace of Mind Through Proactive Care",
      description: "Imagine never having to worry about system outages or performance issues again:",
      features: [
        {
          feature: "24/7 Guardian Services",
          description: "Your systems are continuously monitored, identifying and resolving issues before they impact your business operations."
        },
        {
          feature: "Automatic Maintenance",
          description: "Updates and optimizations happen behind the scenes with minimal disruption, keeping your systems running at peak performance."
        },
        {
          feature: "Insights That Matter",
          description: "Regular reports show exactly how your technology is performing, with clear recommendations for future improvements."
        }
      ],
      testimonial: "For the first time in years, I can sleep without my phone by my bed waiting for the next IT emergency. The proactive approach has virtually eliminated our downtime.",
      author: "CIO, Manufacturing Company"
    },
    {
      icon: Wifi,
      title: "Seamless Connectivity Everywhere",
      description: "Experience reliable, secure, and optimized networks that support your business growth:",
      features: [
        {
          feature: "Custom-Built Networks",
          description: "Your network is designed specifically for your business needs, ensuring optimal performance for your critical applications."
        },
        {
          feature: "Work From Anywhere",
          description: "Your team can access systems securely from any location, supporting flexible work arrangements without compromising security."
        },
        {
          feature: "No More Slowdowns",
          description: "Smart traffic management ensures your most important applications always have the bandwidth they need when they need it."
        }
      ],
      testimonial: "Our team went from constantly complaining about slow applications to forgetting the network even exists—it just works, no matter where they're working from.",
      author: "IT Director, Financial Services Firm"
    },
    {
      icon: Database,
      title: "Cloud That Works For You",
      description: "Harness the full potential of cloud technology while keeping costs under control:",
      features: [
        {
          feature: "Smart Cloud Adoption",
          description: "Your journey to the cloud happens at the right pace for your business, with minimal disruption and maximum benefit."
        },
        {
          feature: "Budget-Friendly Cloud",
          description: "Regular optimization of your cloud environment typically reduces your monthly costs by 20-30% while improving performance."
        },
        {
          feature: "Simplified Multi-Cloud",
          description: "Your entire cloud ecosystem—whether AWS, Azure, Google Cloud, or hybrid—is managed through one consistent approach."
        }
      ],
      testimonial: "I was shocked when our monthly cloud bill dropped by a third after optimization, and even more surprised when application performance improved at the same time.",
      author: "CTO, SaaS Company"
    },
    {
      icon: Laptop,
      title: "Empowered & Productive Teams",
      description: "Give your employees the responsive support they need to stay focused on their work:",
      features: [
        {
          feature: "Always-Available Support",
          description: "Your team gets fast, friendly help through multiple channels, with most issues resolved on the first contact."
        },
        {
          feature: "Lifecycle Device Management",
          description: "Your computers, mobile devices, and peripherals are managed from purchase to retirement, ensuring you always have the right tools."
        },
        {
          feature: "Productivity Tool Mastery",
          description: "Your investment in tools like Microsoft 365 or Google Workspace delivers maximum value through proper configuration and user training."
        }
      ],
      testimonial: "Our employees used to joke about how long they'd have to wait for IT help. Now they rave about how quickly their issues are resolved and how they can focus on their actual jobs.",
      author: "HR Director, Professional Services Firm"
    }
  ];

  return (
    <section className="py-16 bg-gray-50" id="services-overview">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-edgefi-purple/10 text-edgefi-purple rounded-full text-sm font-medium mb-5">Your Path Forward</span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
            Transform How Your Business Experiences IT
          </h2>
          <p className="text-xl text-gray-600">
            Imagine technology that enhances your business instead of holding it back—reliable solutions that optimize performance, reduce costs, and support your growth.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <ServiceCard 
                key={index}
                icon={service.icon}
                title={service.title}
                description={service.description}
                features={service.features}
                testimonial={service.testimonial}
                author={service.author}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
